<template>
  <div class="col-span-6 datav-border rounded-lg p-4 datav-gradient" style="height: 100%; display: flex; flex-direction: column; overflow: hidden;">
    <AlarmNotification ref="alarmNotification" @notificationClosed="handleNotificationClosed" />
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-lg font-semibold flex items-center gap-2">
        <i class="fa fa-thermometer-half text-primary"></i>市区发射基地动环监测
      </h2>
      <div class="text-sm text-gray-300">
        <i class="fa fa-refresh mr-1"></i>最后更新: {{ lastUpdate }}
      </div>
    </div>

    <!-- 设备状态概览 -->
    <div class="grid grid-cols-4 gap-4 mb-6">
      <div class="bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg p-3 flex items-center">
        <div class="w-10 h-10 rounded-full bg-blue-500/30 flex items-center justify-center mr-3">
          <i class="fa fa-check text-blue-300"></i>
        </div>
        <div>
          <div class="text-xs text-blue-300">正常设备</div>
          <div class="text-xl font-bold">{{ normalCount }}</div>
        </div>
      </div>
      <div class="bg-gradient-to-r from-yellow-900 to-yellow-800 rounded-lg p-3 flex items-center">
        <div class="w-10 h-10 rounded-full bg-yellow-500/30 flex items-center justify-center mr-3">
          <i class="fa fa-exclamation text-yellow-300"></i>
        </div>
        <div>
          <div class="text-xs text-yellow-300">警告设备</div>
          <div class="text-xl font-bold">{{ warningCount }}</div>
        </div>
      </div>
      <div class="bg-gradient-to-r from-red-900 to-red-800 rounded-lg p-3 flex items-center">
        <div class="w-10 h-10 rounded-full bg-red-500/30 flex items-center justify-center mr-3">
          <i class="fa fa-times text-red-300"></i>
        </div>
        <div>
          <div class="text-xs text-red-300">告警设备</div>
          <div class="text-xl font-bold">{{ alarmCount }}</div>
        </div>
      </div>
      <div class="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-3 flex items-center">
        <div class="w-10 h-10 rounded-full bg-gray-500/30 flex items-center justify-center mr-3">
          <i class="fa fa-wifi text-gray-300"></i>
        </div>
        <div>
          <div class="text-xs text-gray-300">离线设备</div>
          <div class="text-xl font-bold">{{ offlineCount }}</div>
        </div>
      </div>
    </div>

    <!-- 设备数据卡片网格 -->
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 overflow-auto flex-1 p-1">
      <div v-for="device in devices" :key="device.id" class="device-card" :class="[device.statusClass, { 'col-span-2': device.typeId === 5 || device.typeId === 7 }]">
        <div class="device-header">
          <!-- <span class="device-id">{{ device.id }}</span> -->
          <span class="device-id">{{ device.name }}</span>
          <span class="device-status-indicator" :class="device.statusIndicator"></span>
        </div>
        <div class="device-body">
          <div class="metrics-grid" :class="device.typeId === 7 ? 'metrics-grid-3' : 'metrics-grid'">
            <div class="metric-item" v-if="device.typeId === 1 || device.typeId === 6">
              <div class="metric-label">温度</div>
              <div class="metric-value" :style="{ color: device.tempColor }">{{ device.temp }}</div>
            </div>
            <div class="metric-item" v-if="device.typeId === 1 || device.typeId === 6">
              <div class="metric-label">湿度</div>
              <div class="metric-value" :style="{ color: device.humidityColor }">{{ device.humidity }}</div>
            </div>
            <div class="metric-item" v-if="device.typeId === 2 || device.typeId === 6">
              <div class="metric-label">烟感</div>
              <div class="metric-value" :style="{ color: device.yanganColor }">{{ device.yanganName }}</div>
            </div>
            <div class="metric-item" v-if="device.typeId === 5">
              <div class="metric-label">电压</div>
              <div class="metric-value">
                <div><span class="text-color">uab</span> <span class="value-color">{{ device.uab }}V</span></div>
                <div><span class="text-color">uac</span> <span class="value-color">{{ device.uac }}V</span></div>
                <div><span class="text-color">ubc</span> <span class="value-color">{{ device.ubc }}V</span></div>
              </div>
            </div>
            <div class="metric-item" v-if="device.typeId === 5">
              <div class="metric-label">电流</div>
              <div class="metric-value">
                <div><span class="text-color">ia</span> <span class="value-color">{{ device.ia }}A</span></div>
                <div><span class="text-color">ib</span> <span class="value-color">{{ device.ib }}A</span></div>
                <div><span class="text-color">ic</span> <span class="value-color">{{ device.ic }}A</span></div>
              </div>
            </div>
            
            <div class="metric-item" v-if="device.typeId === 7">
              <div class="metric-label">电压</div>
              <div class="metric-value">
                <div><span class="text-color">uab</span> <span class="value-color">{{ device.uab }}V</span></div>
                <div><span class="text-color">uac</span> <span class="value-color">{{ device.uac }}V</span></div>
                <div><span class="text-color">ubc</span> <span class="value-color">{{ device.ubc }}V</span></div>
              </div>
            </div>
            <div class="metric-item" v-if="device.typeId === 7">
              <div class="metric-label">电流</div>
              <div class="metric-value">
                <div><span class="text-color">ia</span> <span class="value-color">{{ device.ia }}A</span></div>
                <div><span class="text-color">ib</span> <span class="value-color">{{ device.ib }}A</span></div>
                <div><span class="text-color">ic</span> <span class="value-color">{{ device.ic }}A</span></div>
              </div>
            </div>
            <div class="metric-item" v-if="device.typeId === 7">
              <div class="metric-label">温度</div>
              <div class="metric-value">
                <div><span class="text-color">ta</span> <span class="value-color">{{ device.ta }}℃</span></div>
                <div><span class="text-color">tb</span> <span class="value-color">{{ device.tb }}℃</span></div>
                <div><span class="text-color">tc</span> <span class="value-color">{{ device.tc }}℃</span></div>
                <div><span class="text-color">tc</span> <span class="value-color">{{ device.tn }}℃</span></div>
              </div>
            </div>

          </div>
        </div>
        <div class="device-footer">
          <span class="update-time">{{ device.updateTime }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import api from '@/utils/api';
import AlarmNotification from './AlarmNotification';
import audioUtil from '@/utils/audio';
export default {
  name: 'DeviceLayoutCity',
  components: {
    AlarmNotification
  },
  data() {
    return {
      devices: [],
      lastUpdate: '',
      isRefreshing: false,
      alarmedDevices: new Set() // 跟踪已告警的设备，避免重复告警
    };
  },
  computed: {
    normalCount() {
      return this.devices.filter(device => device.status === 'normal').length;
    },
    warningCount() {
      return this.devices.filter(device => device.status === 'warning').length;
    },
    alarmCount() {
      return this.devices.filter(device => device.status === 'alarm').length;
    },
    offlineCount() {
      return this.devices.filter(device => device.status === 'offline').length;
    }
  },
  mounted() {
    // 初始化音频系统，处理浏览器自动播放限制
    audioUtil.initAudioSystem();
    
    this.fetchDeviceData();
    // 设置5秒定时更新数据
    this.updateInterval = setInterval(() => {
      this.fetchDeviceData();
    }, 5000);
  },
  beforeDestroy() {
    clearInterval(this.updateInterval);
  },
  methods: {
    // 处理通知关闭事件
    handleNotificationClosed(notification) {
      if (notification) {
        // 从通知标题中提取设备名称和状态
        const titleParts = notification.title.split(' - ');
        if (titleParts.length === 2) {
          const statusPart = titleParts[0];
          const deviceName = titleParts[1];
          
          // 找到对应的设备
          const device = this.devices.find(d => d.name === deviceName);
          if (device) {
            // 构建deviceKey并停止对应的持续告警声音
            const status = statusPart === '告警' ? 'alarm' : 'warning';
            const deviceKey = `${device.id}_${status}`;
            audioUtil.stopContinuousAlarmSound(deviceKey);
            // 从告警列表中移除
            this.alarmedDevices.delete(deviceKey);
          }
        }
      }
    },
    
    // 从API获取设备数据
    async fetchDeviceData() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      const url = api.devices.getAllGroups();
      try {
        const response = await axios.get(url);
        this.$store.commit('SET_DEVICE_DATA', response.data);
        // 提取设备ID数组并保存到Vuex
        const deviceIds = response.data.data.flatMap(item => (item.devices || []).filter(device => device.typeId === 1 || device.typeId === 6).map(device => device.id));
        this.$store.commit('SET_DEVICE_IDS', deviceIds);

        const rawDevices = response.data.data[1].devices || [];
        this.lastUpdate = response.data.data[1].callTime;
        // 转换数据结构
        this.devices = rawDevices
          .sort((a, b) => {
            // 烟雾设备排在前面
            const aHasSmoke = [2, 6].includes(a.typeId);
            const bHasSmoke = [2, 6].includes(b.typeId);
            
            // typeId=7的设备排在最后
            const aIsType7 = a.typeId === 7;
            const bIsType7 = b.typeId === 7;
            
            // 如果一个是烟雾设备，另一个不是，则烟雾设备排在前面
            if (aHasSmoke && !bHasSmoke) return -1;
            if (!aHasSmoke && bHasSmoke) return 1;
            
            // 如果一个是typeId=7，另一个不是，则typeId=7排在后面
            if (aIsType7 && !bIsType7) return 1;
            if (!aIsType7 && bIsType7) return -1;
            
            // 其他情况保持原有顺序
            return 0;
          })
          .map(device => {
            // 处理温度和湿度值
            const temp = device.wendu ? `${device.wendu}°C` : 'N/A';
            const humidity = device.shidu ? `${device.shidu}%` : 'N/A';
            // 根据阈值判断设备状态
            let status = 'normal';
            if (!device.online) {
              status = 'offline';
            } else if (device.wendu > 30 || device.shidu > 80 || device.yangan >= 1000) {
              status = 'alarm';
            } else if (device.wendu > 25 || device.shidu > 70) {
              status = 'warning';
            }

            // 设置状态样式类
            const statusClass = `status-${status}`;
            const statusIndicator = `status-indicator-${status}`;

            // 设置温度湿度显示颜色
            const tempColor = device.wendu > 30 ? '#ff4d4f' : device.wendu > 25 ? '#faad14' : '#52c41a';
            const humidityColor = device.shidu > 80 ? '#ff4d4f' : device.shidu > 70 ? '#faad14' : '#52c41a';
            const yanganColor = device.yangan != undefined && device.yangan < 1000 ? '#52c41a' : '#ff4d4f';
            const yanganName = device.yangan != undefined && device.yangan < 1000 ? '正常' : '警告';

            // 检测告警状态并触发通知，只在烟感告警时触发
            if (device.yangan >= 1000) {
              const deviceKey = `${device.id}_alarm`;
              if (!this.alarmedDevices.has(deviceKey)) {
                this.alarmedDevices.add(deviceKey);
                
                // 构建告警消息
                let message = '烟感告警';
                
                // 显示告警通知
                if (this.$refs.alarmNotification) {
                  this.$refs.alarmNotification.showNotification(
                    `告警 - ${device.name}`,
                    message,
                    'alarm'
                  );
                }
                
                // 播放告警声音
                audioUtil.playAlarmSound();
                // 开始持续播放告警声音
                audioUtil.startContinuousAlarmSound(deviceKey);
                
                // 播放语音提示，朗读具体点位和报警类型
                audioUtil.playVoiceNotification(device.name, message, 'alarm');
              }
            } else {
              // 设备恢复正常，从告警列表中移除
              const alarmKey = `${device.id}_alarm`;
              const warningKey = `${device.id}_warning`;
              this.alarmedDevices.delete(alarmKey);
              this.alarmedDevices.delete(warningKey);
            }

            return {
              id: device.id,
              name: device.name,
              status,
              statusClass,
              statusIndicator,
              temp,
              humidity,
              typeId: device.typeId,
              yanganName,
              yanganColor,
              tempColor,
              humidityColor,
              updateTime: device.lastUpdateDate,
              uab: device.uab,
              uac: device.uac,
              ubc: device.ubc,
              ia: device.ia,
              ib: device.ib,
              ic: device.ic,
              ta: device.ta,
              tb: device.tb,
              tc: device.tc,
              tn: device.tn,
            };
          });
      } catch (error) {
        this.devices = [];
        throw new Error('获取设备数据失败，请检查API连接');
      } finally {
        this.isRefreshing = false;
      }
    },


  }
}
</script>

<style lang="scss" scoped>
/* 设备卡片样式 */
.device-card {
  background: linear-gradient(
    135deg,
    rgba(30, 41, 59, 0.8) 0%,
    rgba(15, 23, 42, 0.9) 100%
  );
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
}

.device-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* 设备状态样式 */
.status-normal {
  border-color: rgba(82, 196, 26, 0.3);
}

.status-warning {
  border-color: rgba(250, 173, 20, 0.3);
}

.status-alarm {
  border-color: rgba(255, 77, 79, 0.3);
  animation: pulse 2s infinite;
}

.status-offline {
  border-color: rgba(107, 114, 128, 0.3);
  opacity: 0.7;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 77, 79, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0);
  }
}

/* 状态指示器 */
.device-status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator-normal {
  background-color: #52c41a;
  box-shadow: 0 0 6px rgba(82, 196, 26, 0.6);
}

.status-indicator-warning {
  background-color: #faad14;
  box-shadow: 0 0 6px rgba(250, 173, 20, 0.6);
}

.status-indicator-alarm {
  background-color: #ff4d4f;
  box-shadow: 0 0 6px rgba(255, 77, 79, 0.6);
}

.status-indicator-offline {
  background-color: #6b7280;
  box-shadow: 0 0 6px rgba(107, 114, 128, 0.6);
}

/* 设备卡片内部布局 */
.device-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.device-id {
  font-weight: 600;
  color: #e0e0e0;
  font-size: 14px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.metrics-grid-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}

.metric-label {
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 18px;
  font-weight: 500;
  transition: color 0.3s ease;
  .text-color {
    display: inline-block;
    width: 35px;
    margin-right: 8px;
    color: #9ca3af;
  }
  .value-color {
    display: inline-block;
    width: 60px;
    color: rgb(82, 196, 26);
    font-weight: bold;
  }
}

.device-footer {
  margin-top: 12px;
  font-size: 11px;
  color: #6b7280;
  text-align: right;
}
</style>
